import * as React from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Grid,
  Paper,
  Button,
  Typography,
  Box,
  useMediaQuery,
  Chip
} from "@mui/material";
import EmployerNavbar from "app/components/EmployerNavbar";
import { colors } from "styles/colors";
import theme from "styles/theme";
import { getUserInfo } from "utils/helpers";
import { clarityEvent } from "utils/hooks/useClarity";
import { useQuery, useMutation } from "@apollo/client";
import { GET_JOB } from "app/graphql/queries/employer_user/jobs";
import { DELETE_JOBS, UPDATE_JOB_STATUS } from "app/graphql/mutations/jobs";
import getStore from "store/zustandStore";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import SocialMediaShare from "app/components/SocialMediaShareModal";
import JobInfo from "./JobInfo";
import JobDescription from "./JobDescription";
import JobMetaData from "./JobMetaData";
import SalaryDetails from "./SalaryDetails";
import QualificationDetails from "./QualificationDetails";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useSnackbar } from "notistack";

const JobViewDetails = () => {
  const history = useHistory();
  const { job_id }: any = useParams();
  const setLoading = getStore((state: any) => state.setLoading);
  const matchesXs = useMediaQuery(theme.breakpoints.only("xs"));
  const { enqueueSnackbar } = useSnackbar();
  const userInfo = getUserInfo();

  const [job_details, setJobDetails] = React.useState<any>({});
  const [socialShareModelOpen, setSocialShareModelOpen] = React.useState(false);

  const {
    data: job,
    loading: jobLoading,
    refetch
  } = useQuery(GET_JOB, {
    variables: {
      id: job_id
    },
    skip: !job_id,
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only"
  });

  const [update_job_status, { loading: updateJobStatusLoading }] = useMutation(
    UPDATE_JOB_STATUS,
    {
      onCompleted: (data) => {
        const { update_job_status } = data;
        const { status, error } = update_job_status;
        if (status) {
          if (userInfo.id) {
            clarityEvent("job_detail_status_changed", {
              userId: userInfo.id,
              jobId: job_id?.toString() || "unknown"
            });
          }
          refetch();
          enqueueSnackbar(error, {
            variant: "success"
          });
        } else {
          enqueueSnackbar(error, {
            variant: "error"
          });
        }
      },
      fetchPolicy: "network-only"
    }
  );

  const [delete_job, { loading: deleteJobLoading }] = useMutation(DELETE_JOBS, {
    onCompleted: (data) => {
      const { delete_job } = data;
      const { status, error } = delete_job;
      if (status) {
        if (userInfo.id) {
          clarityEvent("job_detail_job_deleted", {
            userId: userInfo.id,
            jobId: job_id?.toString() || "unknown"
          });
        }
        refetch();
        enqueueSnackbar(error, {
          variant: "success"
        });
      } else {
        enqueueSnackbar(error, {
          variant: "error"
        });
      }
    },
    fetchPolicy: "network-only"
  });

  React.useEffect(() => {
    setLoading(jobLoading);
  }, [jobLoading, setLoading]);

  React.useEffect(() => {
    if (userInfo.id && job?.job) {
      clarityEvent("job_detail_page_viewed", {
        userId: userInfo.id,
        jobId: job.job.id?.toString() || "unknown",
        jobTitle: job.job.title || "unknown",
        jobType: job.job.job_type || "unknown",
        jobStatus: job.job.status?.toString() || "unknown",
        totalApplicants: job.job.applied_count?.toString() || "0"
      });
    }
  }, [userInfo.id, job]);

  return (
    <>
      <Box
        sx={{
          display: matchesXs ? "flex" : "none",
          alignItems: "center",
          color: colors.primary,
          marginBottom: 2
        }}
        onClick={() => history.goBack()}
      >
        <KeyboardArrowLeftIcon fontSize="small" color="inherit" />
        <Typography
          fontSize={14}
          sx={{ mr: 23.5, color: colors.fontPrimary, cursor: "pointer" }}
        >
          {" "}
          Go Back
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item lg={12} md={12} sm={12} xs={12} sx={{ display: "flex" }}>
          <Typography
            fontSize="19px"
            fontWeight={500}
            color={colors.fontLighter2}
            onClick={() => history.push("/employer-search-job")}
            sx={{ cursor: "pointer" }}
          >
            Jobs/ Internships
          </Typography>
          <Box sx={{ color: colors.primaryBlue, mx: 1 }}>
            <ChevronRightIcon color="inherit" />
          </Box>
          <Typography
            fontSize="20px"
            fontWeight={500}
            color={colors.fontDarkBlue}
          >
            {job?.job?.title || ""}
          </Typography>
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Paper elevation={2} sx={{ p: 3.5 }}>
            <JobInfo
              job={job}
              refetch={refetch}
              socialShareModelOpen={socialShareModelOpen}
              setSocialShareModelOpen={setSocialShareModelOpen}
              update_job_status={update_job_status}
              delete_job={delete_job}
            />
            <JobDescription job={job} />
            <div
              style={{
                height: 1,
                backgroundColor: "#E9E9E9",
                width: "100%"
              }}
            />
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              sx={{ display: "flex", alignItems: "center", my: 3 }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  color={colors.fontLighter2}
                  fontSize="13px"
                  // ml={0.5}
                  mr={1}
                >
                  Published To:
                </Typography>
                {job?.job?.publish_to === "CAMPUS" &&
                job?.job?.job_campus?.length > 0 ? (
                  job?.job?.job_campus?.map((item) => (
                    <Grid key={item?.campus?.name} item>
                      <Chip
                        label={item?.campus?.name}
                        sx={{
                          backgroundColor: colors.lightBlueChip,
                          color: colors.primaryBlue,
                          borderRadius: "2px"
                        }}
                      />
                    </Grid>
                  ))
                ) : job?.job?.publish_to === "LOCATION" &&
                  job?.job?.job_states?.length > 0 ? (
                  job?.job?.job_states?.map((item) => (
                    <Grid key={item?.state?.name} item>
                      <Chip
                        label={item?.state?.name}
                        sx={{
                          backgroundColor: colors.lightBlueChip,
                          color: colors.primaryBlue,
                          borderRadius: "2px"
                        }}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item>
                    <Chip
                      label={"Everyone"}
                      sx={{
                        backgroundColor: colors.lightBlueChip,
                        color: colors.primaryBlue,
                        borderRadius: "2px"
                      }}
                    />
                  </Grid>
                )}
              </Box>
            </Grid>
            <div
              style={{
                height: 1,
                backgroundColor: "#E9E9E9",
                width: "100%"
              }}
            />
            <JobMetaData job={job} />
            <div
              style={{
                height: 1,
                backgroundColor: "#E9E9E9",
                width: "100%"
              }}
            />
            <SalaryDetails job={job} />
            <div
              style={{
                height: 1,
                backgroundColor: "#E9E9E9",
                width: "100%"
              }}
            />
            <QualificationDetails job={job} />
            <Box
              sx={{
                width: "100%",
                display: matchesXs ? "flex" : "none",
                justifyContent: "space-between"
              }}
            >
              <Box
                sx={{
                  display: "flex"
                }}
              >
                <Button
                  variant="outlined"
                  sx={{
                    color: colors.secondary,
                    backgroundColor: colors.primaryBlue,
                    width: "130px",
                    borderRadius: "1px",
                    "&:hover": {
                      backgroundColor: colors.primaryBlueDark,
                      color: colors.secondary
                    }
                  }}
                  onClick={() => {
                    if (userInfo.id) {
                      clarityEvent("job_detail_view_applicants_clicked", {
                        userId: userInfo.id,
                        jobId: job_id?.toString() || "unknown",
                        applicantCount: job?.job?.applied_count?.toString() || "0"
                      });
                    }
                    history.push(`/employer-job-appliedcanditate/${job_id}`);
                  }}
                >
                  Applicants
                </Button>
              </Box>
              {job?.job?.status == 1 && job?.job?.publish_to?.toLowerCase?.() === "everyone" && <SocialMediaShare
                open={socialShareModelOpen}
                setOpen={setSocialShareModelOpen}
                id={job?.job?.id}
              />}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export const EmployerJobViewDetails = (props) => {
  return <EmployerNavbar mainComponent={<JobViewDetails />} />;
};
